import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { Editor } from 'react-draft-wysiwyg'
import { EditorState, convertToRaw, ContentState, AtomicBlockUtils } from 'draft-js'
import draftToHtml from 'draftjs-to-html'
import htmlToDraft from 'html-to-draftjs'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import { useHistory } from 'react-router-dom'
import apiPost from 'lib/network/apiPost'
import apiPut from 'lib/network/apiPut'
import apiGet from 'lib/network/apiGet'
import apiDelete from 'lib/network/apiDelete'
import notify from 'components/atoms/Notify'
import Switch from 'components/atoms/Switch'
import Selection from 'components/molecules/Selection'
import LoadingIcon from 'components/atoms/LoadingIcon'
import { PhotographIcon, MusicNoteIcon, DocumentIcon } from '@heroicons/react/outline'

import TextArea from 'antd/lib/input/TextArea'
import classNames from 'classnames'
import moment from 'moment'
import { includes, isString, forEach, trim, toLower, keys, startsWith } from 'lodash'
import { getScheduleDate, isFormScheduled } from 'utils/blogPublish'

const BlogProfile = ({
  readOnly,
  refetch,
  loading,
  blogData: initialBlogData,
  isEdit,
  id,
  setBlogData: setParentBlogData,
  onSubmitRef,
  submitActionRef,
  onPublishChange,
  onScheduleChange,
  onSubmittingChange,
}) => {
  const history = useHistory()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(isEdit && !initialBlogData)
  const [editorState, setEditorState] = useState(EditorState.createEmpty())
  const [editorStateAr, setEditorStateAr] = useState(EditorState.createEmpty())
  const [blogData, setBlogData] = useState(initialBlogData || null)
  const [categories, setCategories] = useState([])
  const [showEditorImageGallery, setShowEditorImageGallery] = useState(false)
  const [selectedEditorImages, setSelectedEditorImages] = useState([])
  const [uploadedImages, setUploadedImages] = useState([])
  const [uploadedAudio, setUploadedAudio] = useState([])
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [selectedImageFiles, setSelectedImageFiles] = useState([])
  const [selectedAudioFiles, setSelectedAudioFiles] = useState([])
  const [selectedFileFiles, setSelectedFileFiles] = useState([])
  const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024

  const imageInputRef = useRef(null)
  const editorImageInputRef = useRef(null)
  const audioInputRef = useRef(null)
  const fileInputRef = useRef(null)
  const pendingScheduleRef = useRef('')

  // Sample categories - replace with API call if needed
  const defaultCategories = [
    { id: 'technology', name: 'Technology' },
    { id: 'business', name: 'Business' },
    { id: 'lifestyle', name: 'Lifestyle' },
    { id: 'health', name: 'Health' },
    { id: 'education', name: 'Education' },
    { id: 'entertainment', name: 'Entertainment' },
  ]

  useEffect(() => {
    if (initialBlogData && keys(initialBlogData).length > 0) {
      setBlogData(initialBlogData)
      if (initialBlogData.content) {
        const contentBlock = htmlToDraft(initialBlogData.content)
        if (contentBlock) {
          const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks)
          const editorState = EditorState.createWithContent(contentState)
          setEditorState(editorState)
        }
      }
      if (initialBlogData.contentAr) {
        const contentBlockAr = htmlToDraft(initialBlogData.contentAr)
        if (contentBlockAr) {
          const contentStateAr = ContentState.createFromBlockArray(contentBlockAr.contentBlocks)
          const editorStateAr = EditorState.createWithContent(contentStateAr)
          setEditorStateAr(editorStateAr)
        }
      }
    } else if (isEdit && id && !initialBlogData) {
      // Only fetch if we don't have initial data
      fetchBlogData()
    }
    fetchCategories()
  }, [id, initialBlogData, isEdit])

  useEffect(() => {
    const scheduleDate = getScheduleDate(blogData)
    if (scheduleDate) {
      pendingScheduleRef.current = moment(scheduleDate).format('YYYY-MM-DDTHH:mm')
    }
  }, [blogData?.effectivePublishDate])

  const fetchBlogData = async () => {
    try {
      setIsLoading(true)
      const response = await apiGet(`blog/${id}`, {}, false)
      if (response?.success && response?.data) {
        const data = response.data
        setBlogData(data)
        if (setParentBlogData) {
          setParentBlogData(data)
        }

        // Convert HTML content to EditorState
        if (data.content) {
          const contentBlock = htmlToDraft(data.content)
          if (contentBlock) {
            const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks)
            const editorState = EditorState.createWithContent(contentState)
            setEditorState(editorState)
          }
        }
        if (data.contentAr) {
          const contentBlockAr = htmlToDraft(data.contentAr)
          if (contentBlockAr) {
            const contentStateAr = ContentState.createFromBlockArray(contentBlockAr.contentBlocks)
            const editorStateAr = EditorState.createWithContent(contentStateAr)
            setEditorStateAr(editorStateAr)
          }
        }
      } else {
        notify.error('Failed to load blog')
        history.push('/blogs')
      }
    } catch (error) {
      notify.error('Error loading blog')
      history.push('/blogs')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchCategories = async () => {
    // If you have an API endpoint for categories, use it here
    // For now, using default categories
    setCategories(defaultCategories)
  }

  // Function to generate slug from title
  const generateSlugFromTitle = title => {
    if (!title) return ''
    return toLower(title)
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
  }

  const validateFileSize = (file, label = 'File') => {
    if (file?.size >= MAX_FILE_SIZE_BYTES) {
      notify.error(`${label} size should be less than 50 MB`)
      return false
    }
    return true
  }

  // Memoize initialValues to prevent unnecessary reinitialization
  const initialValues = useMemo(() => {
    const scheduleDate = getScheduleDate(blogData)
    const published = blogData?.published === true

    return {
      title: blogData?.title || '',
      description: blogData?.description || '',
      titleAr: blogData?.titleAr || '',
      descriptionAr: blogData?.descriptionAr || '',
      category: blogData?.category || '',
      published,
      effectivePublishDate: isFormScheduled(published, scheduleDate) ? moment(scheduleDate).format('YYYY-MM-DDTHH:mm') : '',
      sendToNewsletterSubscribers: false,
      slug: blogData?.slug || (blogData?.title ? generateSlugFromTitle(blogData.title) : ''),
    }
  }, [blogData?.title, blogData?.description, blogData?.titleAr, blogData?.descriptionAr, blogData?.category, blogData?.effectivePublishDate, blogData?.published, blogData?.slug])

  const validationSchema = Yup.object({
    title: Yup.string()
      .transform(value => (isString(value) ? trim(value) : value))
      .required('Title is required')
      .min(1, 'Title cannot be empty'),
    description: Yup.string()
      .transform(value => (isString(value) ? trim(value) : value))
      .required('Description is required')
      .min(1, 'Description cannot be empty'),
    titleAr: Yup.string()
      .transform(value => (isString(value) ? trim(value) : value))
      .required('Arabic Title is required')
      .min(1, 'Arabic Title cannot be empty'),
    descriptionAr: Yup.string()
      .transform(value => (isString(value) ? trim(value) : value))
      .required('Arabic Description is required')
      .min(1, 'Arabic Description cannot be empty'),
    category: Yup.string().required('Category is required'),
    slug: Yup.string()
      .transform(value => (isString(value) ? trim(value) : value))
      .required('Slug is required')
      .min(1, 'Slug cannot be empty'),
  })

  const handleSubmit = async (values, { resetForm: formikResetForm, setFieldError }) => {
    try {
      setIsSubmitting(true)
      if (onSubmittingChange) onSubmittingChange(true)

      // Validate content is not empty
      const contentText = trim(editorState.getCurrentContent().getPlainText())
      if (!contentText || contentText.length === 0) {
        setFieldError('content', 'Content is required')
        setIsSubmitting(false)
        return
      }

      // Validate Arabic content is not empty
      const contentArText = trim(editorStateAr.getCurrentContent().getPlainText())
      if (!contentArText || contentArText.length === 0) {
        setFieldError('contentAr', 'Arabic Content is required')
        setIsSubmitting(false)
        return
      }

      // Convert editor content to HTML
      let contentHtml = draftToHtml(convertToRaw(editorState.getCurrentContent()))
      let contentArHtml = draftToHtml(convertToRaw(editorStateAr.getCurrentContent()))

      // Convert blob URLs to base64 in content
      contentHtml = await processContentForBlobUrls(contentHtml)
      contentArHtml = await processContentForBlobUrls(contentArHtml)

      // Slug is required and must be provided
      const slug = trim(values.slug)

      // Create FormData for multipart/form-data request
      const formData = new FormData()

      // Prepare field values
      const titleValue = trim(values.title) || ''
      const descriptionValue = trim(values.description) || ''
      const titleArValue = trim(values.titleAr) || ''
      const descriptionArValue = trim(values.descriptionAr) || ''
      const categoryValue = trim(values.category) || ''
      const slugValue = trim(slug) || ''

      // Always send all fields - backend checks: if (title), if (content), if (description !== undefined), etc.
      // Backend needs these fields to be present in req.body, even if empty
      if (titleValue) formData.append('title', titleValue)
      if (contentHtml) formData.append('content', contentHtml)
      if (titleArValue) formData.append('titleAr', titleArValue)
      if (contentArHtml) formData.append('contentAr', contentArHtml)
      // Always send these fields (backend checks !== undefined, so field must exist)
      formData.append('description', descriptionValue)
      formData.append('descriptionAr', descriptionArValue)
      formData.append('category', categoryValue)

      const submitAction = submitActionRef?.current || 'publish'
      if (submitAction === 'schedule') {
        if (!values.effectivePublishDate) {
          notify.error('Please select a publication date and time')
          setIsSubmitting(false)
          if (onSubmittingChange) onSubmittingChange(false)
          return
        }
        const scheduledDate = new Date(values.effectivePublishDate)
        if (scheduledDate <= new Date()) {
          notify.error('Scheduled publication time must be in the future')
          setIsSubmitting(false)
          if (onSubmittingChange) onSubmittingChange(false)
          return
        }
        formData.append('effectivePublishDate', scheduledDate.toISOString())
        formData.append('published', false)
      } else if (submitAction === 'publish') {
        formData.append('published', true)
      } else {
        formData.append('published', values.published || false)
      }

      formData.append('sendToNewsletterSubscribers', values.sendToNewsletterSubscribers || false)
      formData.append('slug', slugValue)

      // Append image files with field name "images"
      forEach(selectedImageFiles, imageFile => {
        formData.append('images', imageFile.file)
      })

      // Append audio files with field name "audio"
      forEach(selectedAudioFiles, audioFile => {
        formData.append('audio', audioFile.file)
      })

      // Append file files with field name "files"
      forEach(selectedFileFiles, fileFile => {
        formData.append('files', fileFile.file)
      })

      let response
      if (isEdit) {
        response = await apiPut(`blog/${id}`, formData)
      } else {
        response = await apiPost('blog', formData)
      }

      if (response?.success) {
        notify.success(
          submitAction === 'schedule'
            ? isEdit
              ? 'Blog schedule updated successfully'
              : 'Blog scheduled successfully'
            : isEdit
            ? 'Blog updated successfully'
            : 'Blog created successfully'
        )

        // Clean up preview URLs for images
        forEach(selectedImageFiles, item => URL.revokeObjectURL(item.previewUrl))

        // Always redirect to blogs listing page after save
        history.push('/blogs')
      } else {
        notify.error(response?.message || response?.err || 'Failed to save blog')
      }
    } catch (error) {
      notify.error(error?.message || 'Error saving blog')
    } finally {
      setIsSubmitting(false)
      if (onSubmittingChange) onSubmittingChange(false)
    }
  }

  const onEditorStateChange = newEditorState => {
    setEditorState(newEditorState)
  }

  const onEditorStateArChange = newEditorStateAr => {
    setEditorStateAr(newEditorStateAr)
  }

  const blobUrlToBase64 = async blobUrl => {
    try {
      const response = await fetch(blobUrl)
      const blob = await response.blob()
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })
    } catch (error) {
      return blobUrl
    }
  }

  const processContentForBlobUrls = async htmlContent => {
    if (!htmlContent) return htmlContent

    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = htmlContent

    const images = tempDiv.querySelectorAll('img')
    const blobUrlPromises = []

    forEach(Array.from(images), img => {
      const src = img.getAttribute('src')
      if (src && startsWith(src, 'blob:')) {
        blobUrlPromises.push(
          blobUrlToBase64(src).then(base64 => {
            img.setAttribute('src', base64)
          })
        )
      }
    })

    // Wait for all conversions to complete
    await Promise.all(blobUrlPromises)

    return tempDiv.innerHTML
  }

  // Function to insert image into editor at current cursor position
  const insertImageIntoEditor = imageUrl => {
    setEditorState(currentEditorState => {
      const currentSelection = currentEditorState.getSelection()
      const collapsedSelection = currentSelection.merge({
        anchorOffset: currentSelection.getFocusOffset(),
        focusOffset: currentSelection.getFocusOffset(),
      })
      const editorStateWithCollapsedSelection = EditorState.forceSelection(currentEditorState, collapsedSelection)
      const contentState = editorStateWithCollapsedSelection.getCurrentContent()
      const contentStateWithEntity = contentState.createEntity('IMAGE', 'IMMUTABLE', { src: imageUrl })
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey()
      const newEditorState = AtomicBlockUtils.insertAtomicBlock(editorStateWithCollapsedSelection, entityKey, ' ')
      return newEditorState
    })
  }

  // Handle image file selection for editor
  const handleEditorImageFileSelect = file => {
    if (file) {
      const previewUrl = URL.createObjectURL(file)
      insertImageIntoEditor(previewUrl)
    }
  }

  // Handle delete uploaded image
  const handleDeleteImage = async imageUrl => {
    if (!isEdit || !id) return

    try {
      // Encode the image URL for the path parameter
      const encodedUrl = encodeURIComponent(imageUrl)
      const response = await apiDelete(`blog/${id}/image/${encodedUrl}`)
      if (response?.success) {
        notify.success('Image deleted successfully')
        // Update local state
        if (blogData) {
          const updatedImages = blogData.images?.filter(url => url !== imageUrl) || []
          setBlogData({ ...blogData, images: updatedImages })
          if (setParentBlogData) {
            setParentBlogData({ ...blogData, images: updatedImages })
          }
        }
        // Refetch blog data if refetch function is provided
        if (refetch) {
          refetch()
        }
      } else {
        notify.error(response?.message || 'Failed to delete image')
      }
    } catch (error) {
      notify.error(error?.message || 'Error deleting image')
    }
  }

  // Handle delete uploaded audio
  const handleDeleteAudio = async audioUrl => {
    if (!isEdit || !id) return

    try {
      // Encode the audio URL for the path parameter
      const encodedUrl = encodeURIComponent(audioUrl)
      const response = await apiDelete(`blog/${id}/audio/${encodedUrl}`)
      if (response?.success) {
        notify.success('Audio deleted successfully')
        // Update local state
        if (blogData) {
          const updatedAudio = blogData.audio?.filter(url => url !== audioUrl) || []
          setBlogData({ ...blogData, audio: updatedAudio })
          if (setParentBlogData) {
            setParentBlogData({ ...blogData, audio: updatedAudio })
          }
        }
        // Refetch blog data if refetch function is provided
        if (refetch) {
          refetch()
        }
      } else {
        notify.error(response?.message || 'Failed to delete audio')
      }
    } catch (error) {
      notify.error(error?.message || 'Error deleting audio')
    }
  }

  // Handle delete uploaded file
  const handleDeleteFile = async fileUrl => {
    if (!isEdit || !id) return

    try {
      // Encode the file URL for the path parameter
      const encodedUrl = encodeURIComponent(fileUrl)
      const response = await apiDelete(`blog/${id}/file/${encodedUrl}`)
      if (response?.success) {
        notify.success('File deleted successfully')
        // Update local state
        if (blogData) {
          const updatedFiles = blogData.files?.filter(url => url !== fileUrl) || []
          setBlogData({ ...blogData, files: updatedFiles })
          if (setParentBlogData) {
            setParentBlogData({ ...blogData, files: updatedFiles })
          }
        }
        // Refetch blog data if refetch function is provided
        if (refetch) {
          refetch()
        }
      } else {
        notify.error(response?.message || 'Failed to delete file')
      }
    } catch (error) {
      notify.error(error?.message || 'Error deleting file')
    }
  }

  const handleCancelSchedule = async () => {
    if (!isEdit || !id) return

    try {
      setIsSubmitting(true)
      if (onSubmittingChange) onSubmittingChange(true)
      const response = await apiPut(`blog/${id}`, { cancelSchedule: true, published: false })
      if (response?.success) {
        notify.success('Scheduled publication cancelled')
        pendingScheduleRef.current = ''
        if (blogData) {
          const updatedData = { ...blogData, effectivePublishDate: null }
          setBlogData(updatedData)
          if (setParentBlogData) setParentBlogData(updatedData)
        }
        if (refetch) refetch()
      } else {
        notify.error(response?.message || 'Failed to cancel schedule')
      }
    } catch (error) {
      notify.error(error?.message || 'Error cancelling schedule')
    } finally {
      setIsSubmitting(false)
      if (onSubmittingChange) onSubmittingChange(false)
    }
  }

  if (isLoading || loading) {
    return (
      <div className="w-full min-h-[60vh] flex justify-center items-center">
        <LoadingIcon />
      </div>
    )
  }

  return (
    <Formik
      innerRef={form => (onSubmitRef ? (onSubmitRef.current = form) : null)}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize={isEdit}
      validationSchema={validationSchema}
    >
      {({ values, setFieldValue, errors, touched, submitCount, handleSubmit: formikSubmit, resetForm }) => {
        // Generate slug from title on initial load and when title changes
        useEffect(() => {
          if (values.title) {
            const generatedSlug = generateSlugFromTitle(values.title)
            if (generatedSlug !== values.slug) {
              setFieldValue('slug', generatedSlug, false)
            }
          } else if (!values.title && values.slug) {
            setFieldValue('slug', '', false)
          }
        }, [values.title])

        // Update published state in parent
        useEffect(() => {
          if (onPublishChange) {
            onPublishChange(values.published || false)
          }
        }, [values.published, onPublishChange])

        useEffect(() => {
          if (onScheduleChange) {
            onScheduleChange(values.effectivePublishDate || '')
          }
        }, [values.effectivePublishDate, onScheduleChange])

        const isScheduled = isFormScheduled(values.published, values.effectivePublishDate)

        return (
          <React.Fragment>
            <div className="w-full mb-[60px] flex flex-col">
              <div className="bg-white flex w-full">
                <Form className="space-y-8 py-8 w-full mx-auto px-8">
                  {/* Settings Section */}
                  <div className="border-b border-gray-200 pb-6 space-y-6">
                    <h3 className="text-lg font-semibold text-gray-900">Settings</h3>

                    {/* Category */}
                    <div>
                      <label htmlFor="category-select" className="block text-sm font-medium text-gray-700 mb-2">
                        Category <span className="text-red-500">*</span>
                      </label>
                      <div id="category-select">
                        <Selection
                          onChange={selectedValue => {
                            setFieldValue('category', selectedValue?.id || '')
                          }}
                          title="Select a category"
                          multiple={false}
                          list={categories}
                          value={values.category}
                          className="w-full max-w-md"
                          disabled={readOnly}
                        />
                      </div>
                      {submitCount > 0 && errors.category && <p className="mt-1 text-sm text-red-600">{isString(errors.category) ? errors.category : 'Category is required'}</p>}
                    </div>

                    {/* Slug */}
                    <div>
                      <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-2">
                        Slug (URL-friendly identifier) <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="slug"
                        name="slug"
                        type="text"
                        value={isString(values.slug) ? values.slug : String(values.slug || '')}
                        disabled
                        placeholder="blog-post-slug"
                        className="max-w-md block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 cursor-not-allowed sm:text-sm"
                      />
                      {submitCount > 0 && errors.slug && <p className="mt-1 text-sm text-red-600">{isString(errors.slug) ? errors.slug : 'Invalid slug'}</p>}
                      <p className="mt-1 text-sm text-gray-500">Auto-generated from title</p>
                    </div>

                    {/* Featured Image - Read Only */}
                    {isEdit && blogData?.featuredImage && (
                      <div>
                        <div className="block text-sm font-medium text-gray-700 mb-2">Featured Image</div>
                        <div className="inline-block">
                          <img src={blogData.featuredImage} alt="Featured" className="w-48 h-32 object-cover rounded-lg border border-gray-300" />
                        </div>
                        <p className="mt-1 text-sm text-gray-500">This is the main image for the blog post</p>
                      </div>
                    )}

                    {/* Published Toggle */}
                    <div className="flex items-center justify-between">
                      <div>
                        <label htmlFor="published-toggle" className="block text-sm font-medium text-gray-700">
                          Publish
                        </label>
                        <p className="mt-1 text-sm text-gray-500">Make this blog visible to readers immediately</p>
                      </div>
                      <div id="published-toggle">
                        <Switch
                          value={values.published || false}
                          onChange={checked => {
                            if (checked) {
                              if (values.effectivePublishDate) {
                                pendingScheduleRef.current = values.effectivePublishDate
                              }
                              setFieldValue('effectivePublishDate', '', false)
                            } else if (pendingScheduleRef.current) {
                              setFieldValue('effectivePublishDate', pendingScheduleRef.current, false)
                            }
                            setFieldValue('published', checked, false)
                          }}
                          disabled={readOnly}
                        />
                      </div>
                    </div>

                    {/* Schedule Publication */}
                    <div>
                      <label htmlFor="scheduled-publish-at" className="block text-sm font-medium text-gray-700 mb-2">
                        Schedule Publication
                      </label>
                      <input
                        id="scheduled-publish-at"
                        name="effectivePublishDate"
                        type="datetime-local"
                        value={values.effectivePublishDate || ''}
                        onChange={e => {
                          const value = e.target.value
                          pendingScheduleRef.current = value
                          setFieldValue('effectivePublishDate', value)
                        }}
                        disabled={readOnly || values.published}
                        min={moment().format('YYYY-MM-DDTHH:mm')}
                        className="max-w-md block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                      <p className="mt-1 text-sm text-gray-500">Select a date and time, then click Schedule to publish automatically</p>
                      {isScheduled && (
                        <p className="mt-2 text-sm text-blue-600 font-medium">Scheduled for {moment(values.effectivePublishDate).format('MMM D, YYYY [at] h:mm A')}</p>
                      )}
                      {isEdit && isScheduled && !readOnly && (
                        <button type="button" onClick={handleCancelSchedule} disabled={isSubmitting} className="mt-2 text-sm text-red-600 hover:text-red-700 font-medium">
                          Cancel scheduled publication
                        </button>
                      )}
                    </div>

                    {/* Newsletter Toggle */}
                    <div className="flex items-center justify-between">
                      <div>
                        <label htmlFor="newsletter-toggle" className="block text-sm font-medium text-gray-700">
                          Send to all newsletter subscribers
                        </label>
                        <p className="mt-1 text-sm text-gray-500">When published, email this blog to all active newsletter subscribers</p>
                      </div>
                      <div id="newsletter-toggle">
                        <Switch
                          value={values.sendToNewsletterSubscribers || false}
                          onChange={checked => {
                            setFieldValue('sendToNewsletterSubscribers', checked, false)
                          }}
                          disabled={readOnly}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Title */}
                  <div>
                    {values.title && trim(values.title) && (
                      <label htmlFor="title-input" className="block text-sm font-medium text-gray-700 mb-2">
                        Title <span className="text-red-500">*</span>
                      </label>
                    )}
                    <input
                      id="title-input"
                      name="title"
                      type="text"
                      value={isString(values.title) ? values.title : String(values.title || '')}
                      onChange={e => {
                        const titleValue = e.target.value || ''
                        setFieldValue('title', titleValue)
                        const generatedSlug = generateSlugFromTitle(titleValue)
                        setFieldValue('slug', generatedSlug)
                      }}
                      placeholder="Title *"
                      className="text-4xl font-bold border-0 focus:ring-0 p-0 placeholder-gray-400 w-full bg-transparent"
                      disabled={readOnly}
                    />
                    {submitCount > 0 && errors.title && <p className="mt-1 text-sm text-red-600">{isString(errors.title) ? errors.title : 'Title is required'}</p>}
                  </div>

                  {/* Description */}
                  <div>
                    {values.description && trim(values.description) && (
                      <label htmlFor="description-input" className="block text-sm font-medium text-gray-700 mb-2">
                        Description <span className="text-red-500">*</span>
                      </label>
                    )}
                    <TextArea
                      id="description-input"
                      name="description"
                      value={values.description}
                      onChange={e => setFieldValue('description', e.target.value)}
                      placeholder="Tell your story... *"
                      rows={3}
                      className="w-full border-0 focus:ring-0 p-0 text-lg text-gray-600 placeholder-gray-400 resize-none"
                      disabled={readOnly}
                    />
                    {submitCount > 0 && errors.description && (
                      <p className="mt-1 text-sm text-red-600">{isString(errors.description) ? errors.description : 'Description is required'}</p>
                    )}
                  </div>

                  {/* Title (Arabic) */}
                  <div className="border-t border-gray-200 pt-6">
                    {values.titleAr && trim(values.titleAr) && (
                      <label htmlFor="title-ar-input" className="block text-sm font-medium text-gray-700 mb-2">
                        Title (Arabic) <span className="text-red-500">*</span>
                      </label>
                    )}
                    <input
                      id="title-ar-input"
                      name="titleAr"
                      type="text"
                      value={isString(values.titleAr) ? values.titleAr : String(values.titleAr || '')}
                      onChange={e => {
                        const titleArValue = e.target.value || ''
                        setFieldValue('titleAr', titleArValue)
                      }}
                      placeholder="Title (Arabic) *"
                      className="text-4xl font-bold border-0 focus:ring-0 p-0 placeholder-gray-400 w-full bg-transparent"
                      dir="rtl"
                      disabled={readOnly}
                    />
                    {submitCount > 0 && errors.titleAr && <p className="mt-1 text-sm text-red-600">{isString(errors.titleAr) ? errors.titleAr : 'Arabic Title is required'}</p>}
                  </div>

                  {/* Description (Arabic) */}
                  <div>
                    {values.descriptionAr && trim(values.descriptionAr) && (
                      <label htmlFor="description-ar-input" className="block text-sm font-medium text-gray-700 mb-2">
                        Description (Arabic) <span className="text-red-500">*</span>
                      </label>
                    )}
                    <TextArea
                      id="description-ar-input"
                      name="descriptionAr"
                      value={values.descriptionAr}
                      onChange={e => setFieldValue('descriptionAr', e.target.value)}
                      placeholder="Tell your story... (Arabic) *"
                      rows={3}
                      className="w-full border-0 focus:ring-0 p-0 text-lg text-gray-600 placeholder-gray-400 resize-none"
                      dir="rtl"
                      disabled={readOnly}
                    />
                    {submitCount > 0 && errors.descriptionAr && (
                      <p className="mt-1 text-sm text-red-600">{isString(errors.descriptionAr) ? errors.descriptionAr : 'Arabic Description is required'}</p>
                    )}
                  </div>

                  {/* Rich Text Editor */}
                  <div className="border-t border-gray-200 pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <div className="block text-sm font-medium text-gray-700">
                        Content <span className="text-red-500">*</span>
                      </div>
                      {!isEdit && uploadedImages.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setShowEditorImageGallery(true)}
                          className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
                          disabled={readOnly}
                        >
                          <PhotographIcon className="w-4 h-4" />
                          <span>Insert from Gallery</span>
                        </button>
                      )}
                    </div>
                    <Editor
                      editorState={editorState}
                      wrapperClassName="blog-editor-wrapper"
                      editorClassName="blog-editor min-h-[400px] px-4 py-2 border border-gray-300 rounded-md"
                      toolbarClassName="blog-toolbar border-b border-gray-200"
                      onEditorStateChange={onEditorStateChange}
                      readOnly={readOnly}
                      toolbar={{
                        options: ['inline', 'blockType', 'fontSize', 'fontFamily', 'list', 'textAlign', 'colorPicker', 'link', 'embedded', 'emoji', 'image', 'remove', 'history'],
                        inline: { inDropdown: true },
                        list: { inDropdown: true },
                        textAlign: { inDropdown: true },
                        link: { inDropdown: true },
                        history: { inDropdown: true },
                        image: {
                          uploadCallback: file => {
                            return new Promise((resolve, reject) => {
                              if (file) {
                                const previewUrl = URL.createObjectURL(file)
                                resolve({ data: { link: previewUrl } })
                              } else {
                                reject(new Error('No file provided'))
                              }
                            })
                          },
                          alt: { present: true, mandatory: false },
                          defaultSize: {
                            height: 'auto',
                            width: '100%',
                          },
                          inputAccept: 'image/*',
                          previewImage: true,
                          alignmentEnabled: true,
                          urlEnabled: true,
                          uploadEnabled: true,
                        },
                      }}
                    />
                    <input
                      ref={editorImageInputRef}
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={e => {
                        if (e.target.files?.[0]) {
                          handleEditorImageFileSelect(e.target.files[0])
                        }
                      }}
                      disabled={readOnly}
                    />
                    {submitCount > 0 && errors.content && <p className="mt-1 text-sm text-red-600">{isString(errors.content) ? errors.content : 'Content is required'}</p>}
                  </div>

                  {/* Rich Text Editor (Arabic) */}
                  <div className="border-t border-gray-200 pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <div className="block text-sm font-medium text-gray-700">
                        Content (Arabic) <span className="text-red-500">*</span>
                      </div>
                    </div>
                    <Editor
                      editorState={editorStateAr}
                      wrapperClassName="blog-editor-wrapper"
                      editorClassName="blog-editor min-h-[400px] px-4 py-2 border border-gray-300 rounded-md"
                      toolbarClassName="blog-toolbar border-b border-gray-200"
                      onEditorStateChange={onEditorStateArChange}
                      readOnly={readOnly}
                      toolbar={{
                        options: ['inline', 'blockType', 'fontSize', 'fontFamily', 'list', 'textAlign', 'colorPicker', 'link', 'embedded', 'emoji', 'image', 'remove', 'history'],
                        inline: { inDropdown: true },
                        list: { inDropdown: true },
                        textAlign: { inDropdown: true },
                        link: { inDropdown: true },
                        history: { inDropdown: true },
                        image: {
                          uploadCallback: file => {
                            return new Promise((resolve, reject) => {
                              if (file) {
                                const previewUrl = URL.createObjectURL(file)
                                resolve({ data: { link: previewUrl } })
                              } else {
                                reject(new Error('No file provided'))
                              }
                            })
                          },
                          alt: { present: true, mandatory: false },
                          defaultSize: {
                            height: 'auto',
                            width: '100%',
                          },
                          inputAccept: 'image/*',
                          previewImage: true,
                          alignmentEnabled: true,
                          urlEnabled: true,
                          uploadEnabled: true,
                        },
                      }}
                    />
                    {submitCount > 0 && errors.contentAr && (
                      <p className="mt-1 text-sm text-red-600">{isString(errors.contentAr) ? errors.contentAr : 'Arabic Content is required'}</p>
                    )}
                  </div>

                  {/* Media Uploads */}
                  {!readOnly && (
                    <div className="border-t border-gray-200 pt-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Media</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Image Upload */}
                        <div>
                          <label
                            className={classNames(
                              'flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors',
                              isSubmitting ? 'border-blue-400 bg-blue-50 cursor-wait' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                            )}
                          >
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              {isSubmitting ? <LoadingIcon className="w-10 h-10 mb-2" /> : <PhotographIcon className="w-10 h-10 mb-2 text-gray-400" />}
                              <p className="mb-2 text-sm text-gray-500">
                                {isSubmitting ? (
                                  <span className="font-semibold">Uploading...</span>
                                ) : (
                                  <>
                                    <span className="font-semibold">Upload</span> image
                                  </>
                                )}
                              </p>
                            </div>
                            <input
                              ref={imageInputRef}
                              type="file"
                              className="hidden"
                              accept="image/*"
                              disabled={isSubmitting}
                              onChange={e => {
                                if (e.target.files?.[0]) {
                                  const file = e.target.files[0]
                                  if (!validateFileSize(file, 'Image')) {
                                    e.target.value = ''
                                    return
                                  }
                                  const previewUrl = URL.createObjectURL(file)
                                  setSelectedImageFiles(prev => [...prev, { file, previewUrl, name: file.name }])
                                  e.target.value = ''
                                }
                              }}
                            />
                          </label>
                          {(blogData?.images?.length > 0 || uploadedImages.length > 0 || selectedImageFiles.length > 0) && (
                            <div className="mt-2 grid grid-cols-2 gap-2">
                              {selectedImageFiles.map((item, idx) => (
                                <div key={`selected-${idx}`} className="relative group">
                                  <img src={item.previewUrl} alt={item.name} className="w-full h-20 object-cover rounded" />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      URL.revokeObjectURL(item.previewUrl)
                                      setSelectedImageFiles(prev => prev.filter((_, i) => i !== idx))
                                      if (selectedImageFiles.length === 1 && imageInputRef.current) {
                                        imageInputRef.current.value = ''
                                      }
                                    }}
                                    className="absolute top-1 right-1 bg-red-500 rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                  <div className="absolute bottom-1 left-1 right-1 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded truncate">{item.name}</div>
                                </div>
                              ))}
                              {(blogData?.images || []).map((img, idx) => (
                                <div key={`uploaded-${idx}`} className="relative group">
                                  <img src={img} alt={`Upload ${idx + 1}`} className="w-full h-20 object-cover rounded" />
                                  {isEdit && (
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteImage(img)}
                                      className="absolute top-1 right-1 bg-red-500 rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                                      title="Delete image"
                                    >
                                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  )}
                                </div>
                              ))}
                              {uploadedImages.map((img, idx) => (
                                <div key={`new-uploaded-${idx}`} className="relative group">
                                  <img src={img} alt={`New Upload ${idx + 1}`} className="w-full h-20 object-cover rounded" />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Audio Upload */}
                        <div>
                          <label
                            className={classNames(
                              'flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors',
                              isSubmitting ? 'border-blue-400 bg-blue-50 cursor-wait' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                            )}
                          >
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              {isSubmitting ? <LoadingIcon className="w-10 h-10 mb-2" /> : <MusicNoteIcon className="w-10 h-10 mb-2 text-gray-400" />}
                              <p className="mb-2 text-sm text-gray-500">
                                {isSubmitting ? (
                                  <span className="font-semibold">Uploading...</span>
                                ) : (
                                  <>
                                    <span className="font-semibold">Click to upload</span> audio
                                  </>
                                )}
                              </p>
                            </div>
                            <input
                              ref={audioInputRef}
                              type="file"
                              className="hidden"
                              accept="audio/*"
                              disabled={isSubmitting}
                              onChange={e => {
                                if (e.target.files?.[0]) {
                                  const file = e.target.files[0]
                                  if (!validateFileSize(file, 'Audio')) {
                                    e.target.value = ''
                                    return
                                  }
                                  setSelectedAudioFiles(prev => [...prev, { file, name: file.name, size: file.size }])
                                  e.target.value = ''
                                }
                              }}
                            />
                          </label>
                          {(blogData?.audio?.length > 0 || uploadedAudio.length > 0 || selectedAudioFiles.length > 0) && (
                            <div className="mt-2 space-y-2">
                              {selectedAudioFiles.map((item, idx) => (
                                <div key={`selected-audio-${idx}`} className="text-sm text-gray-600 flex items-center justify-between bg-gray-50 p-2 rounded">
                                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                                    <MusicNoteIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                    <span className="truncate">{item.name}</span>
                                    <span className="text-xs text-gray-400 flex-shrink-0">{(item.size / 1024 / 1024).toFixed(2)} MB</span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedAudioFiles(prev => prev.filter((_, i) => i !== idx))
                                      if (selectedAudioFiles.length === 1 && audioInputRef.current) {
                                        audioInputRef.current.value = ''
                                      }
                                    }}
                                    className="ml-2 text-red-500 hover:text-red-700"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              ))}
                              {(blogData?.audio || []).map((audio, idx) => (
                                <div key={`uploaded-audio-${idx}`} className="text-sm text-gray-600 truncate flex items-center justify-between bg-gray-50 p-2 rounded group">
                                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                                    <MusicNoteIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                    <span className="truncate">Audio {idx + 1}</span>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <a href={audio} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                        />
                                      </svg>
                                    </a>
                                    {isEdit && (
                                      <button type="button" onClick={() => handleDeleteAudio(audio)} className="text-red-500 hover:text-red-700" title="Delete audio">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                              {uploadedAudio.map((audio, idx) => (
                                <div key={`new-uploaded-audio-${idx}`} className="text-sm text-gray-600 truncate flex items-center justify-between bg-gray-50 p-2 rounded group">
                                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                                    <MusicNoteIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                    <span className="truncate">New Audio {idx + 1}</span>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <a href={audio} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                        />
                                      </svg>
                                    </a>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* File Upload */}
                        <div>
                          <label
                            className={classNames(
                              'flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors',
                              isSubmitting ? 'border-blue-400 bg-blue-50 cursor-wait' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                            )}
                          >
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              {isSubmitting ? <LoadingIcon className="w-10 h-10 mb-2" /> : <DocumentIcon className="w-10 h-10 mb-2 text-gray-400" />}
                              <p className="mb-2 text-sm text-gray-500">
                                {isSubmitting ? (
                                  <span className="font-semibold">Uploading...</span>
                                ) : (
                                  <>
                                    <span className="font-semibold">Click to upload</span> file
                                  </>
                                )}
                              </p>
                            </div>
                            <input
                              ref={fileInputRef}
                              type="file"
                              className="hidden"
                              disabled={isSubmitting}
                              onChange={e => {
                                if (e.target.files?.[0]) {
                                  const file = e.target.files[0]
                                  if (!validateFileSize(file)) {
                                    e.target.value = ''
                                    return
                                  }
                                  setSelectedFileFiles(prev => [...prev, { file, name: file.name, size: file.size }])
                                  e.target.value = ''
                                }
                              }}
                            />
                          </label>
                          {(blogData?.files?.length > 0 || uploadedFiles.length > 0 || selectedFileFiles.length > 0) && (
                            <div className="mt-2 space-y-2">
                              {selectedFileFiles.map((item, idx) => (
                                <div key={`selected-file-${idx}`} className="text-sm text-gray-600 flex items-center justify-between bg-gray-50 p-2 rounded">
                                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                                    <DocumentIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                    <span className="truncate">{item.name}</span>
                                    <span className="text-xs text-gray-400 flex-shrink-0">{(item.size / 1024 / 1024).toFixed(2)} MB</span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedFileFiles(prev => prev.filter((_, i) => i !== idx))
                                      if (selectedFileFiles.length === 1 && fileInputRef.current) {
                                        fileInputRef.current.value = ''
                                      }
                                    }}
                                    className="ml-2 text-red-500 hover:text-red-700"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              ))}
                              {(blogData?.files || []).map((file, idx) => (
                                <div key={`uploaded-file-${idx}`} className="text-sm text-gray-600 truncate flex items-center justify-between bg-gray-50 p-2 rounded group">
                                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                                    <DocumentIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                    <span className="truncate">File {idx + 1}</span>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <a href={file} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                        />
                                      </svg>
                                    </a>
                                    {isEdit && (
                                      <button type="button" onClick={() => handleDeleteFile(file)} className="text-red-500 hover:text-red-700" title="Delete file">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                              {uploadedFiles.map((file, idx) => (
                                <div key={`new-uploaded-file-${idx}`} className="text-sm text-gray-600 truncate flex items-center justify-between bg-gray-50 p-2 rounded group">
                                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                                    <DocumentIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                    <span className="truncate">New File {idx + 1}</span>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <a href={file} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                        />
                                      </svg>
                                    </a>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </Form>
              </div>
            </div>

            {/* Editor Image Gallery Modal */}
            {showEditorImageGallery && (
              <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowEditorImageGallery(false)}>
                <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Insert Image into Content</h3>
                      <button type="button" onClick={() => setShowEditorImageGallery(false)} className="text-gray-400 hover:text-gray-600">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    <div className="mb-6">
                      <label
                        className={classNames(
                          'flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors',
                          isSubmitting ? 'border-blue-400 bg-blue-50 cursor-wait' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                        )}
                      >
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <PhotographIcon className="w-10 h-10 mb-2 text-gray-400" />
                          <p className="mb-2 text-sm text-gray-500">
                            <span className="font-semibold">Click to upload</span> new image
                          </p>
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={e => {
                            if (e.target.files?.[0]) {
                              const file = e.target.files[0]
                              if (!validateFileSize(file, 'Image')) {
                                e.target.value = ''
                                return
                              }
                              handleEditorImageFileSelect(file)
                              setShowEditorImageGallery(false)
                            }
                          }}
                        />
                      </label>
                    </div>

                    {((isEdit && blogData?.images?.length > 0) || uploadedImages.length > 0) && (
                      <>
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Or select from gallery:</h4>
                        <div className="grid grid-cols-3 gap-4">
                          {[...(blogData?.images || []), ...uploadedImages].map((img, idx) => (
                            <div
                              key={`editor-gallery-${idx}`}
                              className={classNames(
                                'relative cursor-pointer border-2 rounded-lg overflow-hidden transition-all',
                                includes(selectedEditorImages, img) ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-gray-300'
                              )}
                              onClick={() => {
                                if (includes(selectedEditorImages, img)) {
                                  setSelectedEditorImages(selectedEditorImages.filter(i => i !== img))
                                } else {
                                  setSelectedEditorImages([...selectedEditorImages, img])
                                }
                              }}
                            >
                              <img src={img} alt={`Gallery ${idx + 1}`} className="w-full h-32 object-cover" />
                              {includes(selectedEditorImages, img) && (
                                <div className="absolute inset-0 bg-blue-500 bg-opacity-30 flex items-center justify-center">
                                  <div className="bg-blue-600 rounded-full p-2">
                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {(!isEdit || !blogData?.images?.length) && uploadedImages.length === 0 && (
                      <p className="text-center text-gray-500 py-8">No images available in gallery. Upload an image above.</p>
                    )}

                    <div className="mt-6 flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowEditorImageGallery(false)
                          setSelectedEditorImages([])
                        }}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      {selectedEditorImages.length > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            forEach(selectedEditorImages, img => {
                              insertImageIntoEditor(img)
                            })
                            setShowEditorImageGallery(false)
                            setSelectedEditorImages([])
                          }}
                          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                        >
                          Insert Selected ({selectedEditorImages.length})
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <style jsx global>{`
              .blog-editor-wrapper {
                border: 1px solid #e5e7eb;
                border-radius: 0.375rem;
              }
              .blog-editor {
                min-height: 400px;
                padding: 1rem;
              }
              .blog-toolbar {
                border-bottom: 1px solid #e5e7eb;
                padding: 0.5rem;
              }
              .rdw-editor-toolbar {
                margin-bottom: 0;
              }
            `}</style>
          </React.Fragment>
        )
      }}
    </Formik>
  )
}

export default BlogProfile
